import * as React from 'react'
import styled, { css } from 'styled-components'
import { Link } from '../../../../common/src/components/Link'
import { BodyText, BodyTextSpan, Spacer, TextArea, SmallerText } from '../../../../common/src/components/core'
import { ColorName, Colors } from '../../../../common/src/constants'
import { CascadeState } from './EditBox'
import RichText from './RichText'

interface EditBoxLargeProps {
  onKeyDown?: (e: React.KeyboardEvent<any>) => void
  onFocus?: () => void
  onBlur?: () => void
  error?: string
  isEditing?: boolean
  canEdit: boolean
  canDelete: boolean
  confirmDelete?: boolean | null
  cascadeState?: CascadeState
  deletePrompt?: string
  placeholder?: string
  label?: string | null
  sublabel?: string | JSX.Element | null
  body: string
  borderColor?: ColorName
  labelColor?: ColorName | null
  monospace?: boolean
  additionalActions?: { label: string; onClick: () => void }[] | null
  // called when edit box submitted
  submitEdit?: (body?: string) => void
  style?: React.CSSProperties
  testid?: string
}

interface EditBoxLargeState {
  editBody?: string
}

export default class EditBoxLarge extends React.PureComponent<EditBoxLargeProps, EditBoxLargeState> {
  private textarea: HTMLTextAreaElement | null = null

  constructor(props: EditBoxLarge['props']) {
    super(props)
    this.state = { editBody: props.body }
  }

  static getDerivedStateFromProps(props: EditBoxLargeProps) {
    if (!props.isEditing) {
      return { editBody: props.body }
    }
    return null
  }

  render() {
    const {
      canEdit,
      canDelete,
      label,
      sublabel,
      borderColor,
      labelColor,
      body,
      placeholder,
      cascadeState,
      monospace,
      style,
      additionalActions,
      isEditing,
      error,
      testid,
    } = this.props

    const { editBody } = this.state

    const isCascaded = cascadeState === 'OVERRIDDEN_BY_CHILD' || cascadeState === 'DEFAULT_TO_PARENT'

    const displayBody = isCascaded ? '' : body

    const buttons = this.renderButtons(additionalActions, isEditing, canEdit, canDelete)

    return (
      <EditBoxWrap
        borderColor={!isEditing ? borderColor : undefined}
        isEditing={isEditing}
        style={style}
        data-testid={testid}
      >
        {(label || buttons.length > 0) && (
          <EditBoxHeader isEmpty={displayBody === '' && !isEditing}>
            <BodyText>
              <BodyTextSpan color={labelColor || 'white'}>{label}</BodyTextSpan>
              {sublabel && <BodyTextSpan color={labelColor || 'silver'}> {sublabel} </BodyTextSpan>}
            </BodyText>
            <BodyText>{buttons}</BodyText>
          </EditBoxHeader>
        )}
        {isEditing ? (
          <SameSizeTextArea
            hasError={Boolean(error)}
            monospace={monospace}
            ref={this.textAreaRefBound}
            onKeyDown={this.onKeyDownBound}
            placeholder={placeholder}
            value={editBody}
            onChange={this.onChangeBound}
            rows={displayBody === '' ? 6 : 1}
          />
        ) : (
          <EditBoxBody hasError={Boolean(error)}>
            <RichText monospace={monospace}>{displayBody}</RichText>
          </EditBoxBody>
        )}
        {error && <ErrorText color="coral">{error}</ErrorText>}
      </EditBoxWrap>
    )
  }

  private renderButtons(
    additionalActions: { label: string; onClick: () => void }[] | null | undefined,
    isEditing: boolean | undefined,
    canEdit: boolean,
    canDelete: boolean
  ) {
    const buttons = []
    if (additionalActions) {
      for (const action of additionalActions) {
        buttons.push(
          <Link key={action.label} color="sky" onClick={action.onClick}>
            {action.label}
          </Link>
        )
        buttons.push(<Spacer width={8} key={'space-' + action.label} />)
      }
    }
    if (isEditing) {
      buttons.push(
        <Link key="save" color="silver" onClick={this.updateBound}>
          save
        </Link>
      )
      buttons.push(<Spacer width={8} key="space1" />)
      buttons.push(
        <Link key="esc" color="silver" onClick={this.escBound}>
          &times;
        </Link>
      )
    }
    if (!isEditing && canEdit) {
      buttons.push(
        <Link key="edit" color="silver" onClick={this.startEditingBound}>
          edit
        </Link>
      )
    }
    if (!isEditing && canEdit && canDelete) {
      buttons.push(<Spacer width={8} key="space2" />)
    }
    if (!isEditing && canDelete) {
      buttons.push(
        <Link key="del" color="silver" onClick={this.deleteBound}>
          &times;
        </Link>
      )
    }
    return buttons
  }

  private onChangeBound = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ editBody: ev.target.value })
  }

  private textAreaRefBound = (e: HTMLTextAreaElement) => {
    this.textarea = e
    if (e != null) {
      e.style.height = e.scrollHeight + 'px'
      e.select()
    }
  }

  private startEditingBound = () => {
    if (this.props.onFocus) {
      this.props.onFocus()
    }
  }

  private stopEditingBound = () => {
    if (this.props.onBlur) {
      this.props.onBlur()
    }
  }

  private getVal() {
    return this.textarea!.value.trim()
  }

  private updateBound = () => {
    const body = this.getVal()
    this.update(body)
  }

  private deleteBound = () => {
    const { deletePrompt, confirmDelete } = this.props
    if (!confirmDelete || window.confirm(deletePrompt || 'Delete?')) {
      this.update(undefined)
    }
  }

  private escBound = () => {
    this.stopEditingBound()
  }

  private update(body: string | undefined) {
    const { submitEdit } = this.props
    if (submitEdit) {
      submitEdit(body)
    }
  }

  private onKeyDownBound = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.keyCode === 27) {
      this.escBound()
    } else if (e.keyCode === 13 && !e.shiftKey) {
      e.preventDefault()
      if (this.props.onKeyDown) {
        this.props.onKeyDown(e)
      }
    }
  }
}

const EditBoxWrap = styled.div`
  ${(props: { isEditing?: boolean; borderColor?: ColorName }) => css`
    border-radius: 4px;
    background-color: ${props.isEditing ? 'transparent' : Colors.charcoal};
    padding: 8px 12px 4px 12px;
    margin-bottom: 8px;
    border-left: 2px solid ${props.borderColor ? Colors[props.borderColor] : 'transparent'};
  `};
`

const EditBoxHeader = styled.div`
  ${(props: { isEmpty?: boolean }) => css`
    display: flex;
    justify-content: space-between;
    margin-bottom: ${props.isEmpty ? '0px' : '8px'};
  `};
`

const EditBoxBody = styled(BodyText)`
  ${(props: { hasError?: boolean }) => css`
    white-space: pre-wrap;
    color: ${Colors.silver};
    ${props.hasError && `border-color: ${Colors.coral}`};
  `};
`

const SameSizeTextArea = styled(TextArea)`
  ${(props: { hasError?: boolean }) => css`
    width: calc(100% + 24px);
    border-radius: 4px;
    margin: -13px -13px -15px -13px;
    font-size: 14px;
    padding: 12px 10px 12px 12px;
    ${props.hasError && `border-color: ${Colors.coral}`};
  `};
`

const ErrorText = styled(SmallerText)`
  margin-top: 17px;
`
